import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../../api/authAPI';
import { logger } from '../../utils/logger';
import { identifyUser, trackLoginCompleted, trackSignupCompleted } from '../../utils/analytics';
import { formatUserFacingError } from '../../utils/formatUserFacingError';

/** Ignore malformed API payloads so auth state never crashes the UI. */
function normalizeAuthUser(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  if (raw.user && typeof raw.user === 'object' && !Array.isArray(raw.user)) {
    return raw.user;
  }
  if (raw.id == null && !raw.email && !raw.username) {
    return null;
  }
  return raw;
}

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      logger.debug('authSlice: login thunk called');
      const response = await authAPI.login(credentials);
      logger.debug('authSlice: authAPI.login returned');
      // Tokens are in httpOnly cookies, no need to store in localStorage
      logger.debug('authSlice: Tokens are in httpOnly cookies');
      return response;
    } catch (error) {
      logger.error('authSlice: login failed', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        code: error.code,
      });
      
      // Provide more specific error messages
      let errorMessage = 'Login failed';
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = 'Network error: Cannot reach server. Please check your internet connection or contact support if the problem persists.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Login endpoint not found. Please contact support.';
      } else if (error.response?.status === 405) {
        errorMessage = 'Login method not allowed. Please contact support.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data) {
        errorMessage = error.response.data.error || error.response.data.message || JSON.stringify(error.response.data);
      } else {
        errorMessage = error.message || 'Login failed';
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      // Tokens are in httpOnly cookies, no need to store in localStorage
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Registration failed');
    }
  }
);

export const getUserProfile = createAsyncThunk(
  'auth/getUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getUserProfile();
      return response;
    } catch (error) {
      // Don't clear auth state here - let the reducer handle it based on error type
      return rejectWithValue(error.response?.data || 'Failed to get profile');
    }
  }
);

export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.restoreSession();
      if (!response) {
        return rejectWithValue('unauthenticated');
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to restore session');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      // Refresh token is in httpOnly cookie, sent automatically
      const response = await authAPI.refreshToken();
      // New tokens are set in httpOnly cookies by backend
      return response;
    } catch (error) {
      return rejectWithValue('Token refresh failed');
    }
  }
);

const initialState = {
  user: null,
  // Tokens are in httpOnly cookies, not stored in state
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,  // Will be set based on user data
  loading: true,
  sessionChecked: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      // Clear httpOnly cookies on the server (also works after account deactivation)
      authAPI.logout();
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action) => {
      const payload = action.payload || {};
      const { message: _message, ...userFields } = payload;
      state.user = { ...(state.user || {}), ...userFields };
      if (state.user?.id || state.user?.email) {
        state.isAuthenticated = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionChecked = true;
        const user = normalizeAuthUser(action.payload?.user ?? action.payload);
        if (!user) {
          state.isAuthenticated = false;
          state.user = null;
          state.error = 'Login succeeded but profile data was invalid. Please try again.';
          return;
        }
        state.isAuthenticated = true;
        state.user = user;
        if (user?.id) {
          identifyUser(user.id, { email: user.email, name: user.name });
          trackLoginCompleted(user.id);
        }
        // Tokens are in httpOnly cookies, not stored in state
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = formatUserFacingError(action.payload, 'Login failed');
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionChecked = true;
        const user = normalizeAuthUser(action.payload?.user ?? action.payload);
        if (!user) {
          state.isAuthenticated = false;
          state.user = null;
          state.error = 'Registration succeeded but profile data was invalid. Please sign in again.';
          return;
        }
        state.isAuthenticated = true;
        state.user = user;
        if (user?.id) {
          identifyUser(user.id, { email: user.email, name: user.name });
          trackSignupCompleted(user.id);
        }
        // Tokens are in httpOnly cookies, not stored in state
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = formatUserFacingError(action.payload, 'Registration failed');
      })
      // Get User Profile
      .addCase(getUserProfile.pending, (state) => {
        // Only block the app shell when we have no user yet; profile refetches run in the background.
        if (!state.user) {
          state.loading = true;
        }
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        const user = normalizeAuthUser(action.payload);
        if (!user) {
          state.error = 'Could not read your profile. Please sign in again.';
          return;
        }
        state.user = user;
        state.isAuthenticated = true; // Ensure authenticated state is set
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = formatUserFacingError(action.payload, 'Failed to load profile');
        // Don't clear auth state on getUserProfile failure
        // The user might still be authenticated (e.g., if cookies are valid but API call failed)
        // Only clear auth state on explicit logout or 401 from login endpoint
        // This prevents redirect loops when getUserProfile() fails right after login
      })
      // Restore session from httpOnly cookies after page refresh
      .addCase(restoreSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionChecked = true;
        const user = normalizeAuthUser(action.payload);
        if (!user) {
          state.user = null;
          state.isAuthenticated = false;
          return;
        }
        state.user = user;
        state.isAuthenticated = true;
      })
      .addCase(restoreSession.rejected, (state) => {
        state.loading = false;
        state.sessionChecked = true;
        state.user = null;
        state.isAuthenticated = false;
      })
      // Refresh Token
      .addCase(refreshToken.fulfilled, (_state) => {
        // New tokens are in httpOnly cookies, no need to update state
      })
      .addCase(refreshToken.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        // Cookies will be cleared by backend if refresh fails
      });
  },
});

export const { logout, clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;
